import { format, formatDistanceToNowStrict, isToday, isYesterday } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Formato en español de España. Centralizado a propósito: el día que haya que
 * soportar otro idioma o zona horaria, se cambia aquí y no en 80 componentes.
 */

const dateFmt = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' });
const dateTimeFmt = new Intl.DateTimeFormat('es-ES', {
  dateStyle: 'medium',
  timeStyle: 'short',
});
const currencyFmt = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});
const numberFmt = new Intl.NumberFormat('es-ES');

export const formatDate = (value: string | Date | null | undefined) =>
  value ? dateFmt.format(new Date(value)) : '—';

export const formatDateTime = (value: string | Date | null | undefined) =>
  value ? dateTimeFmt.format(new Date(value)) : '—';

export const formatCurrency = (value: number | null | undefined) =>
  value == null ? '—' : currencyFmt.format(value);

export const formatNumber = (value: number | null | undefined) =>
  value == null ? '—' : numberFmt.format(value);

/**
 * "hace 12 min", "ayer", "12 mar 2026".
 * Lo reciente en relativo (es lo que el usuario necesita para el feed de
 * actividad) y lo antiguo en absoluto (donde "hace 8 meses" no dice nada).
 */
export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();

  // FECHAS EN EL FUTURO. Sin esta rama, `diffMs` sale negativo y la primera
  // comprobación se cumple siempre: la caducidad de un enlace a 30 días vista
  // se mostraba como «caduca ahora mismo», que además de falso asusta.
  if (diffMs < 0) {
    const faltan = -diffMs;
    if (faltan < 60_000) return 'en un momento';
    // A menos de una semana interesa el plazo; más allá, la fecha concreta,
    // que es lo que alguien apunta o compara con un calendario.
    if (faltan < 7 * 24 * 3600_000) {
      return `en ${formatDistanceToNowStrict(date, { locale: es })}`;
    }
    return `el ${dateFmt.format(date)}`;
  }

  if (diffMs < 60_000) return 'ahora mismo';
  if (diffMs < 24 * 3600_000 && isToday(date)) {
    return `hace ${formatDistanceToNowStrict(date, { locale: es })}`;
  }
  if (isYesterday(date)) return `ayer, ${format(date, 'HH:mm')}`;
  if (diffMs < 7 * 24 * 3600_000) {
    return format(date, 'EEEE, HH:mm', { locale: es });
  }
  return dateFmt.format(date);
}

/**
 * Tamaños en base 1024 con unidades SI, que es lo que muestran Windows y
 * macOS y por tanto lo que el usuario espera reconocer.
 */
export function formatBytes(bytes: number | bigint | null | undefined): string {
  if (bytes == null) return '—';
  const n = typeof bytes === 'bigint' ? Number(bytes) : bytes;
  if (n === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const value = n / Math.pow(1024, i);

  return `${value.toLocaleString('es-ES', {
    maximumFractionDigits: value < 10 && i > 0 ? 1 : 0,
  })} ${units[i]}`;
}

export const formatPercent = (value: number | null | undefined) =>
  value == null ? '—' : `${Math.round(value)} %`;

export function initials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}
